import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 ────────────────────────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

const Ico = {
  Search: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Link:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  Close:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

const NoticeBoard = () => {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  const [userName,       setUserName]       = useState('사용자');
  const [university,     setUniversity]     = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage,   setAlertMessage]   = useState('');

  const triggerAlert = (msg) => { setAlertMessage(msg); setShowAlertModal(true); };

  useEffect(() => {
    const userData = localStorage.getItem('currentUser');
    if (userData) {
      try {
        const u = JSON.parse(userData);
        if (u?.name)       setUserName(u.name);
        if (u?.university) setUniversity(u.university);
      } catch (e) {}
    }
  }, []);

  const [notices,        setNotices]        = useState([]);
  const [filter,         setFilter]         = useState('전체');
  const [searchTerm,     setSearchTerm]     = useState('');
  const [searchDate,     setSearchDate]     = useState('');
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [crawled,        setCrawled]        = useState(false);

  const categoryColors = {
    '전체': BLUE,
    '학사': '#ef4444',
    '장학': '#f97316',
    '취업': '#eab308',
    '기술': '#22c55e',
    '정보': '#3b82f6',
    '긴급': '#6366f1',
    '일반': '#a855f7',
  };

  const getCatStyle = (cat) => {
    const colorMap = {
      '학사': { bg: '#fee2e2', color: '#b91c1c' },
      '장학': { bg: '#ffedd5', color: '#c2410c' },
      '취업': { bg: '#fef9c3', color: '#854d0e' },
      '기술': { bg: '#dcfce7', color: '#166534' },
      '정보': { bg: '#dbeafe', color: '#1e40af' },
      '긴급': { bg: '#ede9fe', color: '#5b21b6' },
      '일반': { bg: '#fae8ff', color: '#7e22ce' },
    };
    return colorMap[cat] || { bg: BLUE_BG, color: BLUE };
  };

  useEffect(() => {
    if (!university) return;
    const sessionKey = `crawled_${university}`;
    const alreadyCrawled = sessionStorage.getItem(sessionKey);
    if (!alreadyCrawled) {
      handleAutoRefresh();
    } else {
      fetchNotices();
      setCrawled(true);
    }
  }, [university]);

  const handleAutoRefresh = async () => {
    setLoading(true);
    try {
      await fetch(`https://campus-life-production.up.railway.app/api/notices/crawl?university=${encodeURIComponent(university)}`, { method: 'POST' });
      await fetchNotices();
      sessionStorage.setItem(`crawled_${university}`, 'true');
    } catch (e) {}
    setLoading(false);
  };

  const fetchNotices = async () => {
    if (!university) return;
    setLoading(true);
    try {
      const res  = await fetch(`https://campus-life-production.up.railway.app/api/notices?university=${encodeURIComponent(university)}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setNotices(data.map(n => ({
          id: n.id, title: n.title,
          category: n.category || '일반',
          date: n.noticeDate || '',
          author: university,
          content: n.title,
          isImportant: n.importance === '높음',
          url: n.url,
        })));
      }
    } catch (e) {}
    setLoading(false);
  };

  const handleRefresh = async () => {
    if (crawled) return;
    setLoading(true);
    try {
      await fetch(`https://campus-life-production.up.railway.app/api/notices/crawl?university=${encodeURIComponent(university)}`, { method: 'POST' });
      await fetchNotices();
      setCrawled(true);
      sessionStorage.setItem(`crawled_${university}`, 'true');
      triggerAlert('최신화 완료!');
    } catch (e) {
      triggerAlert('크롤링 실패! 백엔드 서버를 확인해주세요.');
    }
    setLoading(false);
  };

  const getFilteredNotices = () => {
    let list = notices;
    if (filter !== '전체') list = list.filter(n => n.category === filter);
    if (searchTerm) list = list.filter(n => n.title.includes(searchTerm));
    if (searchDate) list = list.filter(n => n.date === searchDate);
    return list;
  };

  const filteredNotices = getFilteredNotices();

  return (
    <div style={{
      ...S.root,
      padding: isMobile ? '16px 14px 60px' : '32px 32px 60px',
      overflowX: 'hidden',
    }}>
      <div style={S.inner}>

        {/* ── 헤더 (모바일: 세로 배치) ── */}
        <div style={{
          ...S.header,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '10px' : '0',
        }}>
          <h1 style={{ ...S.pageTitle, fontSize: isMobile ? '17px' : '22px' }}>
            📋 {university} 공지사항
          </h1>
          <button
            style={{
              ...S.refreshBtn,
              width: isMobile ? '100%' : 'auto',
              backgroundColor: crawled ? '#94a3b8' : BLUE,
              cursor: crawled ? 'not-allowed' : 'pointer',
            }}
            onClick={handleRefresh}
            disabled={crawled || loading}
          >
            {loading ? '최신화 중...' : crawled ? '최신화 완료!' : '최신화 하기 (크롤링)'}
          </button>
        </div>

        {/* ── 검색 카드 ── */}
        <div style={{ ...S.card, padding: isMobile ? '16px 14px' : '20px 22px', overflow: 'hidden' }}>
          <CardHeader icon={Ico.Search} title="공지 검색" />
          <div style={{
            ...S.searchRow,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '10px' : '12px',
          }}>
            <div style={{ ...S.searchGroup, flex: 1, width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}>
              <label style={S.searchLabel}>날짜로 찾기</label>
              <input
                type="date"
                value={searchDate}
                onChange={e => setSearchDate(e.target.value)}
                style={{ ...S.searchInput, width: '100%', maxWidth: '100%', boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none' }}
              />
            </div>
            <div style={{ ...S.searchGroup, flex: 1, width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}>
              <label style={S.searchLabel}>내용으로 찾기</label>
              <input
                placeholder="검색어를 입력하세요 (최대 30자)"
                value={searchTerm}
                onChange={e => { if (e.target.value.length <= 30) setSearchTerm(e.target.value); }}
                style={{ ...S.searchInput, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                maxLength={30}
              />
            </div>
          </div>
        </div>

        {/* ── 탭 + 목록 카드 ── */}
        <div style={S.card}>
          {/* 탭 */}
          <div style={{
            ...S.tabNav,
            gap: isMobile ? '6px' : '8px',
            marginBottom: isMobile ? '12px' : '16px',
          }}>
            {Object.keys(categoryColors).map(cat => (
              <button
                key={cat}
                style={{
                  ...S.tabBtn,
                  padding: isMobile ? '5px 10px' : '6px 14px',
                  fontSize: isMobile ? '11px' : '12px',
                  backgroundColor: filter === cat ? categoryColors[cat] : '#fff',
                  color:           filter === cat ? '#fff' : '#64748b',
                  borderColor:     filter === cat ? categoryColors[cat] : '#e2e8f0',
                  fontWeight:      filter === cat ? '700' : '500',
                }}
                onClick={() => setFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* 목록 헤더 */}
          <div style={S.listHeader}>{filter} 공지 리스트</div>

          {/* 목록 */}
          <div style={S.scrollArea}>
            {loading ? (
              <div style={S.emptyText}>⏳ 불러오는 중...</div>
            ) : filteredNotices.length > 0 ? (
              filteredNotices.map(notice => {
                const cs = getCatStyle(notice.category);
                return (
                  <div key={notice.id} style={S.noticeItem} onClick={() => setSelectedNotice(notice)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      <span style={{ ...S.catTag, backgroundColor: cs.bg, color: cs.color, flexShrink: 0 }}>
                        {notice.category}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          ...S.noticeTitle,
                          // 모바일: 줄임표 처리
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: isMobile ? 'nowrap' : 'normal',
                        }}>
                          {notice.title}
                        </div>
                        <div style={S.noticeMeta}>
                          {isMobile
                            ? notice.date  // 모바일: 날짜만
                            : `작성자: ${notice.author} | ${notice.date}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={S.emptyText}>공지사항이 없습니다.</div>
            )}
          </div>
        </div>

      </div>

      {/* ── 상세 모달 ── */}
      {selectedNotice && (
        <div style={S.modalOverlay} onClick={() => setSelectedNotice(null)}>
          <div style={{
            ...S.modal,
            padding: isMobile ? '20px 18px' : '28px 32px',
            maxHeight: isMobile ? '90vh' : '80vh',
          }} onClick={e => e.stopPropagation()}>

            <div style={S.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                <span style={{ ...S.catTag, ...getCatStyle(selectedNotice.category), flexShrink: 0 }}>
                  {selectedNotice.category}
                </span>
                {selectedNotice.isImportant && (
                  <span style={S.importantTag}>중요</span>
                )}
              </div>
              <button style={S.closeBtn} onClick={() => setSelectedNotice(null)}>
                {Ico.Close}
              </button>
            </div>

            <h2 style={{ ...S.modalTitle, fontSize: isMobile ? '15px' : '18px' }}>
              {selectedNotice.title}
            </h2>

            <div style={S.modalMeta}>
              <span style={S.metaItem}>✍️ {selectedNotice.author}</span>
              <span style={S.metaDivider}>|</span>
              <span style={S.metaItem}>📅 {selectedNotice.date || '날짜 없음'}</span>
            </div>

            <div style={S.modalDivider} />

            <div style={S.modalBody}>
              <p style={S.modalContent}>{selectedNotice.content}</p>
            </div>

            {selectedNotice.url && (
              <a href={selectedNotice.url} target="_blank" rel="noreferrer" style={S.linkBtn}>
                {Ico.Link}
                <span>원문 보러가기</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* ── 알림 모달 ── */}
      {showAlertModal && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '360px' }}>
            <p style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '0 0 20px', textAlign: 'center' }}>{alertMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button style={{ ...S.primaryBtn, flex: 'none', padding: '10px 32px' }} onClick={() => setShowAlertModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh', backgroundColor: '#f8fafc',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '32px 32px 60px', boxSizing: 'border-box',
  },
  inner:      { width: '100%', maxWidth: '1100px', display: 'flex', flexDirection: 'column', gap: '20px' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  pageTitle:  { fontSize: '22px', fontWeight: '800', color: '#1e293b', margin: 0 },
  refreshBtn: { padding: '9px 16px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '20px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', boxSizing: 'border-box',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' },
  cardIcon:   { width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: BLUE_BG, color: BLUE },
  cardTitle:  { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  searchRow:   { display: 'flex', gap: '12px', alignItems: 'flex-end' },
  searchGroup: { display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 },
  searchLabel: { fontSize: '11px', fontWeight: '600', color: '#64748b' },
  searchInput: { padding: '9px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#374151', backgroundColor: '#f8fafc', outline: 'none' },
  tabNav:     { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  tabBtn:     { padding: '6px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', border: '1px solid' },
  listHeader: { fontSize: '13px', fontWeight: '600', color: '#1e293b', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9', marginBottom: '4px' },
  scrollArea: { maxHeight: '500px', overflowY: 'auto' },
  noticeItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0', borderBottom: '1px solid #f1f5f9', gap: '12px', cursor: 'pointer' },
  catTag:     { padding: '3px 9px', borderRadius: '5px', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  noticeTitle:{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '2px' },
  noticeMeta: { fontSize: '11px', color: '#94a3b8' },
  primaryBtn:  { flex: 1, padding: '11px 16px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn:{ flex: 1, padding: '11px 16px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  emptyText:  { textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '30px 0', margin: 0 },
  modalOverlay:{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 },
  modal: {
    backgroundColor: '#fff', borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    width: '90%', maxWidth: '600px',
    padding: '28px 32px',
    display: 'flex', flexDirection: 'column', gap: '0',
    maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box',
  },
  modalHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' },
  closeBtn:     { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '6px', flexShrink: 0 },
  modalTitle:   { margin: '0 0 12px', fontSize: '18px', fontWeight: '700', color: '#1e293b', lineHeight: '1.5' },
  modalMeta:    { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' },
  metaItem:     { fontSize: '12px', color: '#94a3b8' },
  metaDivider:  { fontSize: '12px', color: '#e2e8f0' },
  modalDivider: { height: '1px', backgroundColor: '#f1f5f9', marginBottom: '18px' },
  modalBody:    { flex: 1, minHeight: '100px' },
  modalContent: { margin: 0, fontSize: '14px', lineHeight: '1.85', color: '#374151', whiteSpace: 'pre-wrap' },
  linkBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    marginTop: '20px', padding: '9px 16px',
    backgroundColor: BLUE_BG, color: BLUE,
    borderRadius: '8px', fontSize: '12px', fontWeight: '600',
    textDecoration: 'none', border: `1px solid #bfdbfe`,
  },
  importantTag: { padding: '2px 8px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '5px', fontSize: '11px', fontWeight: '700' },
};

export default NoticeBoard;